import { useMediaQuery } from '@mui/material'
import { useEffect, useState } from 'react'
import theme from 'utils/theme'

export const LAUNCHER_SIZE = 64
// Same at every width: leaves room for the hover aura at the screen edge.
export const LAUNCHER_RIGHT = 24
// Narrow pages often end in a full-width primary button, so start higher there.
export const LAUNCHER_BOTTOM = { narrow: 96, wide: 24 }
const GAP = 16

const PINNED = new Set(['fixed', 'sticky'])

// Height of a bar pinned to the bottom of the viewport in the launcher's column
// (a sticky "Review and pay" footer, say), or 0. Probes the launcher's column
// with elementsFromPoint and walks up to a fixed/sticky (or <footer>) ancestor
// that touches the viewport bottom; tall pinned things (side columns,
// overlays) are ignored.
export function pinnedBottomBarHeight(launcher: HTMLElement | null): number {
  if (typeof document.elementsFromPoint !== 'function') return 0
  const viewportHeight = window.innerHeight
  const x = window.innerWidth - LAUNCHER_RIGHT - LAUNCHER_SIZE / 2
  let height = 0

  document.elementsFromPoint(x, viewportHeight - 2).forEach(hit => {
    if (launcher?.contains(hit)) return
    for (
      let el: Element | null = hit;
      el && el !== document.body;
      el = el.parentElement
    ) {
      // BaseLayout's stickyFooter is an absolutely positioned <footer>.
      const pinned =
        PINNED.has(window.getComputedStyle(el).position) ||
        el.tagName === 'FOOTER'
      if (!pinned) continue
      const rect = el.getBoundingClientRect()
      const touchesBottom = rect.bottom >= viewportHeight - 2
      if (touchesBottom && rect.height <= viewportHeight * 0.4) {
        height = Math.max(height, viewportHeight - rect.top)
      }
      break
    }
  })
  return height
}

// Bottom offset (px) that keeps the launcher clear of pinned bottom CTAs.
export function useLauncherBottom(
  launcherRef: React.RefObject<HTMLElement>
): number {
  const narrow = useMediaQuery(theme.breakpoints.down('sm'))
  const base = narrow ? LAUNCHER_BOTTOM.narrow : LAUNCHER_BOTTOM.wide
  const [barHeight, setBarHeight] = useState(0)

  useEffect(() => {
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() =>
        setBarHeight(pinnedBottomBarHeight(launcherRef.current))
      )
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, { passive: true })
    // Pages mount their sticky footers after data loads.
    const observer = new MutationObserver(measure)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [launcherRef])

  return barHeight > 0 ? Math.max(base, barHeight + GAP) : base
}
